import { HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom, Observable } from 'rxjs';
import { GlideAjaxResponse } from '../models/catalog-form';

// Change operations drained by the consumer after each script run. Matches
// guide §7.3.
export type GFormOp =
  | 'setValue'
  | 'setVisible'
  | 'setMandatory'
  | 'setReadOnly'
  | 'clearOptions'
  | 'addOption'
  | 'showFieldMsg'
  | 'hideFieldMsg'
  | 'addErrorMessage'
  | 'clearMessages';

export interface GFormChange {
  op: GFormOp;
  field: string | null;
  value?: unknown;
  displayValue?: string;
  label?: string;
}

export type GFormScriptType = 'onLoad' | 'onChange' | 'onSubmit';

export interface GFormRunOpts {
  type: GFormScriptType;
  fieldName?: string;
  oldValue?: unknown;
  newValue?: unknown;
  newDisplayValue?: string;
  isLoading?: boolean;
}

// Bridge signature we call for GlideAjax proxying. The concrete implementation
// lives in `GlideAjaxBridgeService`, but the mock takes only the surface it
// needs so unit tests can stub it without dragging in Angular's DI.
export interface GFormGlideAjaxBridge {
  invoke(
    catalogItemSysId: string,
    scriptInclude: string,
    method: string,
    params: Record<string, unknown>,
  ): Observable<GlideAjaxResponse>;
}

// The slice of SN's XHR response that getXML callbacks read.
export interface GlideAjaxXmlResponse {
  responseXML: { documentElement: { getAttribute(name: string): string | null } };
}

function toXmlResponse(answer: unknown): GlideAjaxXmlResponse {
  const value = answer == null ? null : String(answer); // NOSONAR: S6551 — Script Include answers are scalars.
  return {
    responseXML: {
      documentElement: { getAttribute: (name) => (name === 'answer' ? value : null) },
    },
  };
}

// Ported from client-sdk/GFormMock.js. Diffs from the SDK:
//   1. GlideAjax proxy calls `GlideAjaxBridgeService.invoke` (Mulesoft
//      `/catalog/executeGlideAjax`) instead of the SDK's direct SN endpoint.
//   2. Typed: `unknown` at wire boundaries; no `any`.
//   3. `console.info` instead of `console.warn`/`console.error` (project's
//      allowlisted method — see the reset memory's Sonar recipes).
export class GFormMock {
  private readonly _itemSysId: string;
  private readonly _values: Record<string, unknown>;
  private readonly _bridge: GFormGlideAjaxBridge;
  private readonly _allowedScriptIncludes: ReadonlySet<string>;
  private readonly _onScriptIncludeUnavailable: (scriptInclude: string) => void;
  private readonly _displayValueOf: (fieldName: string) => string | undefined;
  private readonly _displayValues: Record<string, string> = {};
  private _pending: GFormChange[] = [];
  private _glideAjaxUnavailable = false;
  // GlideAjax calls still waiting for the proxy. Scripts fire them without
  // awaiting (SN style), so `whenIdle()` is the only way to know they landed.
  private readonly _inFlight = new Set<Promise<unknown>>();

  // `allowedScriptIncludes` is the item's `discoveredSis` from
  // GET /catalog/items/{sysId}/metadata. Mulesoft only proxies Script
  // Includes it auto-discovered, so anything else is never sent. Empty by
  // default: no list, no GlideAjax calls.
  // `onScriptIncludeUnavailable` fires when the proxy answers 403 (not
  // approved by a SNOW admin, or not client_callable) so the form can tell
  // the user without blocking it.
  constructor(opts: {
    itemSysId: string;
    currentValues: Record<string, unknown>;
    bridge: GFormGlideAjaxBridge;
    allowedScriptIncludes?: readonly string[];
    onScriptIncludeUnavailable?: (scriptInclude: string) => void;
    // Display text for a field as the user sees it (reference label, choice
    // label). Backs `g_form.getDisplayValue`; falls back to the raw value.
    displayValueOf?: (fieldName: string) => string | undefined;
  }) {
    this._itemSysId = opts.itemSysId;
    this._values = { ...opts.currentValues };
    this._bridge = opts.bridge;
    this._allowedScriptIncludes = new Set(opts.allowedScriptIncludes ?? []);
    this._onScriptIncludeUnavailable =
      opts.onScriptIncludeUnavailable ?? (() => undefined);
    this._displayValueOf = opts.displayValueOf ?? (() => undefined);
  }

  // -- g_form API surface (called from within sandboxed scripts) ------------

  getValue(fieldName: string): unknown {
    return this._values[fieldName] ?? '';
  }

  // Scripts' own setValue(name, value, display) wins over the form lookup
  // until the next sync from user input.
  getDisplayValue(fieldName: string): string {
    const display = this._displayValues[fieldName] ?? this._displayValueOf(fieldName);
    if (display !== undefined) return display;
    const value = this._values[fieldName];
    return value == null ? '' : String(value); // NOSONAR: S6551 — form values are primitives.
  }

  clearValue(fieldName: string): void {
    this.setValue(fieldName, '');
  }

  setValue(fieldName: string, value: unknown, displayValue?: string): void {
    this._values[fieldName] = value;
    if (displayValue === undefined) delete this._displayValues[fieldName];
    else this._displayValues[fieldName] = displayValue;
    this._pending.push({
      op: 'setValue',
      field: fieldName,
      value,
      displayValue: displayValue ?? String(value ?? ''), // NOSONAR: S6551 — SN client scripts only pass primitive setValue payloads.
    });
  }

  setVisible(fieldName: string, visible: unknown): void {
    this._pending.push({
      op: 'setVisible',
      field: fieldName,
      value: Boolean(visible),
    });
  }

  setMandatory(fieldName: string, mandatory: unknown): void {
    this._pending.push({
      op: 'setMandatory',
      field: fieldName,
      value: Boolean(mandatory),
    });
  }

  setReadOnly(fieldName: string, readOnly: unknown): void {
    this._pending.push({
      op: 'setReadOnly',
      field: fieldName,
      value: Boolean(readOnly),
    });
  }

  // SN aliases setDisplay onto setVisible in most catalog contexts.
  setDisplay(fieldName: string, display: unknown): void {
    this._pending.push({
      op: 'setVisible',
      field: fieldName,
      value: Boolean(display),
    });
  }

  clearOptions(fieldName: string): void {
    this._pending.push({ op: 'clearOptions', field: fieldName });
  }

  addOption(fieldName: string, value: unknown, label: string): void {
    this._pending.push({ op: 'addOption', field: fieldName, value, label });
  }

  // SN signature: showFieldMsg(field, message, type = 'info', scrollForm).
  showFieldMsg(fieldName: string, message: string, type?: string): void {
    this._pending.push({
      op: 'showFieldMsg',
      field: fieldName,
      value: message,
      label: type === 'error' ? 'error' : 'info',
    });
  }

  hideFieldMsg(fieldName: string): void {
    this._pending.push({ op: 'hideFieldMsg', field: fieldName });
  }

  // The catalog item's sys_id on the SN portal; scripts pass it to Script
  // Includes (e.g. `sysparm_catItem`).
  getUniqueValue(): string {
    return this._itemSysId;
  }

  // Backend-form URL parameters do not exist here.
  getParameter(_name: string): string {
    return '';
  }

  addErrorMessage(message: string): void {
    this._pending.push({ op: 'addErrorMessage', field: null, value: message });
  }

  clearMessages(): void {
    this._pending.push({ op: 'clearMessages', field: null });
  }

  // No-ops for DOM-specific SN portal APIs — we log once so a developer
  // seeing a mystery script silently misbehave can trace back to here.
  getFieldElement(_fieldName: string): null {
    console.info('[GFormMock] getFieldElement is not supported — returning null');
    return null;
  }

  flash(_fieldName: string, _color?: string, _count?: number): void {
    // Visual pulse effect only — not applicable outside SN portal.
  }

  // -- GlideAjax proxy ------------------------------------------------------

  // Returns a SN-shaped GlideAjax stub. Consumer scripts call
  // `addParam('sysparm_name', method)` + other `addParam` pairs, then
  // `getXMLAnswer(callback)`. We shim by collecting params, invoking our
  // bridge, and calling the callback with the unwrapped `result` — SN's
  // native `getXMLAnswer` hands the callback the answer directly, so
  // scripts written against the SN portal expect that shape (not the
  // Mulesoft `{ result, warnings }` envelope). Warnings are logged for
  // debugging without breaking the script's callback contract.
  //
  // When the call cannot be made (Script Include not in discoveredSis) or
  // fails, the callback is NOT called: scripts read a missing answer as a
  // negative one (e.g. "The user is not entitled to order the item"), which
  // would be wrong — the answer is unknown, not "no".
  GlideAjax(scriptIncludeName: string): {
    addParam(key: string, value: unknown): void;
    getXMLAnswer(callback: (answer: unknown) => void): Promise<void>;
    getXML(callback: (response: GlideAjaxXmlResponse) => void): Promise<void>;
  } {
    const params: Record<string, unknown> = {};
    let method = '';

    // Resolves the answer, or `undefined` when there is none to hand over.
    const fetchAnswer = (): Promise<unknown> => {
      const call = fetchAnswerOnce();
      this._inFlight.add(call);
      return call.finally(() => this._inFlight.delete(call));
    };
    const fetchAnswerOnce = async (): Promise<unknown> => {
      if (!this._allowedScriptIncludes.has(scriptIncludeName)) {
        console.info(
          `[GFormMock] GlideAjax skipped: ${scriptIncludeName} is not in discoveredSis`,
        );
        this._glideAjaxUnavailable = true;
        return undefined;
      }
      try {
        const response: GlideAjaxResponse = await firstValueFrom(
          this._bridge.invoke(this._itemSysId, scriptIncludeName, method, params),
        );
        if (response?.warnings?.length) {
          console.info('[GFormMock] GlideAjax proxy warnings:', response.warnings);
        }
        return response?.result ?? null;
      } catch (e) {
        console.info('[GFormMock] GlideAjax proxy error:', e);
        this._glideAjaxUnavailable = true;
        if (e instanceof HttpErrorResponse && e.status === 403) {
          this._onScriptIncludeUnavailable(scriptIncludeName);
        }
        return undefined;
      }
    };

    return {
      addParam(key: string, value: unknown) {
        if (key === 'sysparm_name') {
          method = String(value ?? ''); // NOSONAR: S6551 — GlideAjax method names are strings from SN metadata.
        } else {
          params[key] = value;
        }
      },
      // SN hands getXMLAnswer callbacks the answer itself.
      async getXMLAnswer(callback: (answer: unknown) => void): Promise<void> {
        const answer = await fetchAnswer();
        if (answer !== undefined) callback(answer);
      },
      // SN hands getXML callbacks the XHR, read as
      // `response.responseXML.documentElement.getAttribute('answer')`.
      async getXML(callback: (response: GlideAjaxXmlResponse) => void): Promise<void> {
        const answer = await fetchAnswer();
        if (answer !== undefined) callback(toXmlResponse(answer));
      },
    };
  }

  // True once any GlideAjax call on this form could not be answered (not in
  // discoveredSis, refused, or failed). onSubmit checks that depend on such
  // a value cannot pass, so the form does not let them block submit.
  get glideAjaxUnavailable(): boolean {
    return this._glideAjaxUnavailable;
  }

  // Resolves once no GlideAjax call is in flight, including calls that a
  // callback started in turn. Lets onSubmit see the values (or the
  // unavailability) a just-changed field triggered.
  //
  // With `timeoutMs`, stops waiting after that long and counts the calls
  // still pending as unavailable (resolves `false`), so a slow proxy cannot
  // hold the form hostage. Late answers are still applied when they land.
  async whenIdle(timeoutMs?: number): Promise<boolean> {
    // Let synchronous onChange handlers that are still being dispatched
    // start their calls first.
    await Promise.resolve();
    const settle = async (): Promise<boolean> => {
      while (this._inFlight.size > 0) {
        await Promise.allSettled([...this._inFlight]);
      }
      return true;
    };
    if (timeoutMs === undefined) return settle();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timedOut = new Promise<boolean>((resolve) => {
      timer = setTimeout(() => resolve(false), timeoutMs);
    });
    const idle = await Promise.race([settle(), timedOut]);
    clearTimeout(timer);
    if (!idle) this._glideAjaxUnavailable = true;
    return idle;
  }

  // -- Script execution -----------------------------------------------------

  // Sandboxed execution of ServiceNow client scripts (onLoad/onChange/onSubmit).
  //
  // Sonar S1523 (dynamic code execution) — reviewed:
  //  1. Caller-side gate: none any more. INEXP-2383 removed the
  //     `focus_inexp_2496_snow_sdk` kill switch, so `catalog-form.component.ts`
  //     runs client scripts on every catalog form load. The protection now
  //     rests on points 2–4, and GlideAjax only reaches Script Includes in
  //     the item's `discoveredSis` that a SNOW admin approved.
  //  2. Trusted source: `scriptBody` originates only from the Mulesoft-fronted,
  //     Ping-authenticated `/catalog/items/{sysId}/metadata` response
  //     (`clientScripts[].script`), which ServiceNow itself curates and admins
  //     gate via `x_catrender_si_allowlist`. It is never sourced from user
  //     input, URL params, `location.*`, or third-party origins.
  //  3. In-sink defenses (see `_isSafeScriptBody` below): non-string, empty,
  //     and oversized payloads are rejected before compilation. All execution
  //     errors are caught and treated as non-blocking for onLoad/onChange.
  //  4. Sandboxed surface: the generated function runs in strict mode and
  //     receives only the `g_form`, `GlideAjax`, `g_user` and `getMessage`
  //     bindings we choose to expose — no access to `window`, `document`, `fetch`, or
  //     `HttpClient` beyond the GlideAjax bridge, which itself proxies through
  //     the same authenticated Mulesoft endpoint.
  //  5. CSP: the SPA does not currently restrict `unsafe-eval`. If that
  //     changes, this SDK path breaks, and there is no flag to switch it off
  //     (see #1): it needs a code change.
  //
  // Returns `false` only when an onSubmit handler explicitly returned false
  // (blocks submission per guide §7); onLoad/onChange results are ignored.
  async runScript(scriptBody: string, opts: GFormRunOpts): Promise<boolean> {
    if (!this._isSafeScriptBody(scriptBody)) {
      // Non-blocking: unreachable in practice given the caller-side gate, but
      // if a malformed payload ever reaches us we prefer a silent skip to a
      // form that mysteriously refuses to render.
      return true;
    }
    // SN scripts call `new GlideAjax('X')`. A bound class method is not
    // constructible ("GlideAjax is not a constructor"), so expose a plain
    // function: when a constructor returns an object, `new` yields it.
    const glideAjaxFactory = this.GlideAjax.bind(this);
    const glideAjax = function GlideAjax(scriptIncludeName: string) {
      return glideAjaxFactory(scriptIncludeName);
    };
    // SN global `getMessage(key, callback)` — translations are not available
    // here, so the key (SN's English source text) is the message.
    const getMessage = (key: string, callback?: (message: string) => void): string => {
      if (typeof callback === 'function') callback(key);
      return key;
    };
    const gUser = {
      userID: null as string | null,
      userName: null as string | null,
      hasRole: () => false,
    };

    // NOSONAR on the six sinks below — Sonar S1523 (dynamic code execution).
    // Trust chain reviewed in the doc block on `runScript` above (points 1–5).
    try {
      if (opts.type === 'onChange') {
        const changeFn = new Function( // NOSONAR: S1523 — trust chain reviewed
          'g_form',
          'GlideAjax',
          'g_user',
          'getMessage',
          'control',
          'oldValue',
          'newValue',
          'isLoading',
          'isTemplate',
          `"use strict"; ${scriptBody}; if (typeof onChange === 'function') return onChange(control, oldValue, newValue, isLoading, isTemplate);`,
        );
        await changeFn( // NOSONAR: S1523 — trust chain reviewed
          this,
          glideAjax,
          gUser,
          getMessage,
          null,
          opts.oldValue ?? '',
          opts.newValue ?? '',
          opts.isLoading ?? false,
          false,
        );
        return true;
      }

      if (opts.type === 'onLoad') {
        const loadFn = new Function( // NOSONAR: S1523 — trust chain reviewed
          'g_form',
          'GlideAjax',
          'g_user',
          'getMessage',
          `"use strict"; ${scriptBody}; if (typeof onLoad === 'function') return onLoad();`,
        );
        await loadFn(this, glideAjax, gUser, getMessage); // NOSONAR: S1523 — trust chain reviewed
        return true;
      }

      // onSubmit
      const submitFn = new Function( // NOSONAR: S1523 — trust chain reviewed
        'g_form',
        'GlideAjax',
        'g_user',
        'getMessage',
        `"use strict"; ${scriptBody}; if (typeof onSubmit === 'function') return onSubmit();`,
      );
      const result = await submitFn(this, glideAjax, gUser, getMessage); // NOSONAR: S1523 — trust chain reviewed
      return result !== false;
    } catch (e) {
      console.info(
        '[GFormMock] Script execution warning:',
        e instanceof Error ? e.message : String(e),
        '\nScript excerpt:',
        scriptBody.substring(0, SCRIPT_EXCERPT_MAX_CHARS),
      );
      return true;
    }
  }

  // Reject anything that isn't a plausible SN client script before we hand it
  // to `new Function`. Length cap is generous (SN client scripts are typically
  // <5 KiB; the cap exists purely to bound the attack surface if the metadata
  // endpoint is ever compromised to return a mass payload).
  private _isSafeScriptBody(scriptBody: unknown): scriptBody is string {
    return (
      typeof scriptBody === 'string' &&
      scriptBody.trim().length > 0 &&
      scriptBody.length <= MAX_SCRIPT_BODY_CHARS
    );
  }

  // -- State ----------------------------------------------------------------

  drainChanges(): GFormChange[] {
    const changes = this._pending;
    this._pending = [];
    return changes;
  }

  getValues(): Record<string, unknown> {
    return { ...this._values };
  }
}

// Cap the excerpt logged when a script throws — full script bodies can be
// hundreds of lines and swamp the console.
const SCRIPT_EXCERPT_MAX_CHARS = 100;

// Upper bound on client-script payloads accepted by `runScript`. Real SN
// scripts are single-digit KiB; 64 KiB is roomy but keeps the attack surface
// bounded if the metadata channel is ever compromised.
const MAX_SCRIPT_BODY_CHARS = 64 * 1024;
